import { Stacked, StackedGrow } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { metricsAtom } from '@/atoms/metric';
import { playerRadarTemplateConfigsAtom } from '@/atoms/queries/userConfigs';
import { RADAR_VIEW, radarTemplatePositionGroupAtom, radarViewAtom } from '@/atoms/radar';
import { RadarLowMinutesWarning } from '@/components/feedback/RadarLowMinutesWarning';
import { RadarThreeSixtyUnavailableInfoBar } from '@/components/feedback/RadarThreeSixtyUnavailableInfoBar';
import { VisContentsContainer } from '@/components/layout/VisContentsContainer';
import { RadarCard } from '@/components/radar/RadarCard';
import { RadarCardHeader } from '@/components/radar/RadarCardHeader';
import { RadarExportModal } from '@/components/radar/RadarExportModal';
import { PercentileDataPillLegend } from '@/components/radar/table/PercentileDataPillLegend';
import { PlayerRadarTable } from '@/components/radar/table/PlayerRadarTable';
import { RadarTableDisplayProps } from '@/components/radar/table/RadarTable';
import { OverviewHeader } from '@/components/section/OverviewHeader';
import { useIsComparisonActive } from '@/hooks/useIsComparisonActive';
import { PlayerComparisonLegend } from './PlayerComparisonLegend';
import { PlayerRadarChart } from './PlayerRadarChart';

export const PlayerRadarTableCard = ({ isInteractive = true, showSpokes }: RadarTableDisplayProps) => (
  <Stacked direction="column" justifyContent="space-between">
    <PlayerRadarTable isInteractive={isInteractive} showSpokes={showSpokes} />
    <PercentileDataPillLegend />
  </Stacked>
);

export const PlayerRadar = () => {
  const { t } = useTranslation(['radar', 'entity']);
  const isComparisonActive = useIsComparisonActive();
  const isDistributionView = useAtomValue(radarViewAtom) === RADAR_VIEW.DISTRIBUTION;
  const label = t('dropdownLabel', { entity: t('player_one', { ns: 'entity' }) });
  const mainLegend = isComparisonActive && <PlayerComparisonLegend id="main" />;

  return (
    <>
      <div className="flex flex-col gap-2 min-h-full">
        <OverviewHeader radarTemplateConfigsAtom={playerRadarTemplateConfigsAtom} label={label} />
        <VisContentsContainer rowBreakpoint="xl">
          <RadarCard>
            <StackedGrow>
              <Stacked direction="column" gap="regular">
                <RadarCardHeader />
                <Stacked direction="column" gap="tiny">
                  <RadarThreeSixtyUnavailableInfoBar />
                  <RadarLowMinutesWarning />
                </Stacked>
              </Stacked>
            </StackedGrow>
            <ScopeProvider atoms={[metricsAtom, radarTemplatePositionGroupAtom]}>
              <div className="mx-auto max-w-[550px]">
                {/* the legend leads the distribution list but follows the radar */}
                {isDistributionView && mainLegend}
                <PlayerRadarChart id="main" isInteractive />
                {!isDistributionView && mainLegend}
              </div>
            </ScopeProvider>
          </RadarCard>
          {/* I'm the last person to commit here, but don't blame me for this mess!
           This should be the Card component from KC */}
          <div className={twJoin('bg-canvas-tertiary-main flex-1 p-4', isComparisonActive && 'min-w-0')}>
            <PlayerRadarTableCard showSpokes={!isDistributionView} />
          </div>
        </VisContentsContainer>
      </div>

      <RadarExportModal>
        <ScopeProvider atoms={[metricsAtom, radarTemplatePositionGroupAtom]}>
          <div>
            <PlayerRadarChart id="export" isInteractive={false} />
            {isComparisonActive && <PlayerComparisonLegend id="export" />}
          </div>
        </ScopeProvider>
        <PlayerRadarTableCard isInteractive={false} />
      </RadarExportModal>
    </>
  );
};
